import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { BCPPrint } from "../../../components/icons/bcp/BCPIcons";
import ModalLoadingMessage from "../../../components/modals/ModalLoadingMessage";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  findGetParameter,
  getData
} from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import {
  createDoublesTable,
  createSeatedTable,
  createTable,
  createTeamTable
} from "./pairings/PrintPairingsFunctions";
import {
  DoublesPrintRow,
  SeatedPrintRow,
  SinglesPrintRow,
  TeamPrintRow
} from "./pairings/PrintPairingsRows";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);

export function EventPrintPairings(props) {
  const { eventId } = useParams();

  const round = findGetParameter("round");
  const type = findGetParameter("type");
  const submitted = findGetParameter("submitted");

  const [loading, setLoading] = useState(true);
  // const [subLoading, setSubLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [table, setTable] = useState(null);

  // Display these numbers in the print
  const [numOfPlayers, setNumOfPlayers] = useState(null);
  const [numOfTeams, setNumOfTeams] = useState(null);

  const [message, setMessage] = useState(null);

  async function fetchPairings(params) {
    if (!params?.round) return [];

    let returnArray = [];

    try {
      const apiPairings = await getData(
        `/events/${event?.id}/pairings`,
        params
      );

      if (apiPairings?.active?.length) {
        returnArray = apiPairings.active;
      }

      if (event?.format?.doublesEvent) {
        returnArray = await addPlayersToDoubles(returnArray);
      }
    } catch (err) {
      postNotification("error", err);
      return undefined;
    }
    return returnArray;
  }

  async function fetchPlayers(params) {
    try {
      const players = await getData(`/events/${event?.id}/players`, {
        placings: true
      });
      return players?.active;
    } catch (err) {
      postNotification("error", err);
      return undefined;
    }
  }

  async function fetchTeamPlayers(params) {
    try {
      const teamPlayers = await getData(`/events/${event?.id}/teamplayers`, {
        placings: true
      });
      return teamPlayers?.active;
    } catch (err) {
      postNotification("error", err);
      return undefined;
    }
  }

  async function parallel(_event, round) {
    const queryParams = {
      eventId: _event?.id || eventId,
      round: round
    };

    const taskPairings =
      !_event?.format?.boardGameEvent && !_event?.format?.doublesEvent
        ? fetchPairings({
            ...queryParams,
            pairingType: "Pairing",
            ...(submitted ? { isDone: true } : {})
          })
        : [];
    const taskTeamPairings =
      !_event?.format?.boardGameEvent && _event?.format?.teamEvent
        ? fetchPairings({ ...queryParams, pairingType: "TeamPairing" })
        : [];
    const taskSeatedPairings = _event?.format?.boardGameEvent
      ? fetchPlayers({
          ...queryParams,
          pairingType: "SeatedPairing",
          ...(submitted ? { isDone: true } : {})
        })
      : [];
    const taskPlayers = fetchPlayers();
    const taskTeamPlayers =
      event?.format?.teamEvent && !event?.format?.doublesEvent
        ? fetchTeamPlayers()
        : [];

    return {
      pairings: await taskPairings,
      teamPairings: await taskTeamPairings,
      seatedPairings: await taskSeatedPairings,
      players: await taskPlayers,
      teamPlayers: await taskTeamPlayers
    };
  }

  // Adds player data to pairings for team events or doubles events
  async function addPlayersToDoubles(teamPairings) {
    if (!teamPairings || !teamPairings.length) return [];

    let players = [];

    // Fetch players
    try {
      setMessage("Fetching Players...");
      const apiPlayers = await getData(`/events/${event?.id}/players`);

      if (apiPlayers?.active?.length) {
        players = apiPlayers.active;
      }
    } catch (err) {
      postNotification("error", err);
    }

    if (!players || !players.length) return teamPairings;

    // Create doubles pairings by adding players to their respective team pairings
    const newPairings = [...teamPairings];

    players.forEach((player) => {
      newPairings.forEach((pairing) => {
        const playerObj = {
          id: player?.user?.id,
          name: `${player?.user?.firstName} ${player?.user?.lastName}`.trim(),
          firstName: player?.user?.firstName,
          lastName: player?.user?.lastName
        };

        if (player?.teamPlayerId === pairing?.teamPlayer1?.id) {
          if (pairing?.teamPlayer1?.players?.length) {
            pairing.teamPlayer1.players.push(playerObj);
          } else {
            pairing.teamPlayer1.players = [playerObj];
          }
        }

        if (player?.teamPlayerId === pairing?.teamPlayer2?.id) {
          if (pairing?.teamPlayer2?.players?.length) {
            pairing.teamPlayer2.players.push(playerObj);
          } else {
            pairing.teamPlayer2.players = [playerObj];
          }
        }
      });
    });

    return newPairings;
  }

  useEffect(() => {
    if (!eventId) return;

    const fetchEvent = async () => {
      try {
        setMessage("Fetching Event Data...");
        const apiEvent = await getData(`/events/${eventId}/overview`);

        setEvent(apiEvent);
      } catch (err) {
        setMessage(null);
        setLoading(false);
        postNotification("error", err);
      }
    };

    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (event && round !== undefined && round !== null) {
      const buildPage = async () => {
        try {
          setMessage("Fetching Pairings...");
          const parallelData = await parallel(event, parseInt(round));
          setMessage("Generating Table...");

          //  Validate the data before creating the table
          if (event?.format?.teamEvent && !event?.format?.doublesEvent) {
            if (
              !parallelData?.teamPairings ||
              !parallelData?.teamPairings.length
            ) {
              throw new Error("No team pairings found for this round or event");
            }
          } else if (event?.format?.doublesEvent) {
            if (
              !parallelData?.teamPairings ||
              !parallelData?.teamPairings.length
            ) {
              throw new Error("No pairings found for this round or event");
            }
          } else if (event?.format?.boardGameEvent) {
            if (
              !parallelData?.seatedPairings ||
              !parallelData?.seatedPairings.length
            ) {
              throw new Error(
                "No seated pairings found for this round or event"
              );
            }
          } else {
            if (!parallelData?.pairings || !parallelData?.pairings.length) {
              throw new Error("No pairings found for this round or event");
            }
          }

          //  Create the table based on the event format
          const { _table, _numOfPlayers, _numOfTeams } =
            event?.format?.teamEvent && !event?.format?.doublesEvent
              ? createTeamTable(
                  {
                    teamPairings: parallelData.teamPairings,
                    pairings: parallelData.pairings
                  },
                  {
                    teamPlayers: parallelData.teamPlayers,
                    players: parallelData.players
                  },
                  type
                )
              : event?.format?.doublesEvent
                ? createDoublesTable(
                    parallelData.teamPairings,
                    parallelData.teamPlayers,
                    type
                  )
                : event?.format?.boardGameEvent
                  ? createSeatedTable(
                      parallelData.seatedPairings,
                      parallelData.players,
                      type
                    )
                  : createTable(
                      parallelData.pairings,
                      parallelData.players,
                      type
                    );

          setTable(_table);
          setNumOfPlayers(_numOfPlayers);
          if (event?.format?.teamEvent) {
            setNumOfTeams(_numOfTeams);
          }
        } catch (err) {
          postNotification("error", err);
        } finally {
          setMessage(null);
          setLoading(false);
        }
      };

      buildPage();
    }
    // eslint-disable-next-line
  }, [event, round]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        minHeight: "100vh",
        color: "#000"
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          {loading && (
            <ModalLoadingMessage message={message || "Loading..."} open />
          )}

          <Grid
            item
            xs={12}
            sx={{
              "@media print": {
                display: "none"
              },
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center"
            }}
          >
            <Button
              onClick={() => window.print()}
              endIcon={<BCPPrint />}
              color="primary"
              sx={{
                textTransform: "none",
                pr: 2,
                pl: 2,
                pt: 2
              }}
            >
              Print Page
            </Button>
          </Grid>

          <Grid
            item
            sx={{
              display: "flex",
              wordWrap: "break-word",
              flexDirection: "column",
              justifyContent: "flex-end"
            }}
            xs={12}
            sm={8}
          >
            {event?.name && (
              <Typography variant="h2" component="h1" sx={{ fontWeight: 600 }}>
                {event.name}
              </Typography>
            )}

            <Typography variant="h4" component="h2">
              Round {round || event?.status?.currentRound || "N/A"} |
              {" Sort By: "}
              {type === "lastName"
                ? "Last Name A-Z"
                : type === "firstName"
                  ? "First Name A-Z"
                  : type === "tableNumber"
                    ? "Table Number"
                    : type === "teamName"
                      ? "Team Name A-Z"
                      : ""}
            </Typography>
            {submitted && (
              <Typography variant="caption" component="p">
                {`(Submitted Pairings Only)`}
              </Typography>
            )}
            {numOfPlayers || numOfTeams ? (
              <Typography variant="caption" component="p">
                {numOfPlayers ? `Total Players: ${numOfPlayers}` : ""}
                {numOfPlayers && numOfTeams ? " | " : ""}{" "}
                {numOfTeams ? `Total Teams: ${numOfTeams}` : ""}{" "}
              </Typography>
            ) : (
              <></>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: ["flex-start", "flex-end"]
            }}
          >
            <img
              src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogoLarge.png`}
              alt="logo"
              style={{
                maxHeight: "80px",
                maxWidth: "220px"
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            {!loading && !table ? (
              "No pairings for this round or event found"
            ) : (
              <table
                style={{
                  width: "100%",
                  border: "1px solid #000",
                  borderCollapse: "collapse",
                  marginBottom: "1rem"
                }}
              >
                {/* Print styling */}
                <style>
                  {`
        .print-table-head {
          background-color: #dddddd;
          border: 1px solid #000;
          font-weight: 600;
          font-size: 20px;
        }
        .print-table-row-even {
          background-color: #dddddd;
        }

        @media print {
          .print-table-head {
            background-color: #dddddd !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-table-row-even {
            background-color: #dddddd !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}
                </style>

                {event &&
                  table &&
                  table.map((row, index) => {
                    if (row?.isHeader) {
                      if (
                        event?.format?.doublesEvent ||
                        event?.format?.teamEvent
                      ) {
                        return (
                          <thead className="print-table-head" key={index}>
                            <tr>
                              <th style={{ textAlign: "center" }}>Table</th>
                              <th style={{ textAlign: "start" }}>
                                Player / Team
                              </th>
                              <th style={{ textAlign: "center" }}>Points</th>
                              <th style={{ textAlign: "center" }}></th>
                              <th style={{ textAlign: "center" }}>Points</th>
                              <th style={{ textAlign: "start" }}>Opponent</th>
                            </tr>
                          </thead>
                        );
                      } else if (event?.format?.boardGameEvent) {
                        return (
                          <thead className="print-table-head" key={index}>
                            <tr>
                              <th style={{ textAlign: "center" }}>Table</th>
                              <th style={{ textAlign: "start" }}>Player</th>
                              <th style={{ textAlign: "start" }}>Seat</th>
                              <th style={{ textAlign: "center" }}>Points</th>
                              <th style={{ textAlign: "center" }}>Result</th>
                            </tr>
                          </thead>
                        );
                      } else {
                        return (
                          <thead className="print-table-head" key={index}>
                            <tr>
                              <th style={{ textAlign: "center" }}>Table</th>
                              <th style={{ textAlign: "start" }}>Player</th>
                              <th style={{ textAlign: "center" }}>Points</th>
                              <th style={{ textAlign: "center" }}></th>
                              <th style={{ textAlign: "center" }}>Points</th>
                              <th style={{ textAlign: "start" }}>Opponent</th>
                            </tr>
                          </thead>
                        );
                      }
                    }

                    return (
                      <tbody>
                        {event?.format?.doublesEvent ? (
                          <DoublesPrintRow
                            event={event}
                            row={row}
                            key={index}
                          />
                        ) : event?.format?.teamEvent ? (
                          <TeamPrintRow event={event} row={row} key={index} />
                        ) : event?.format?.boardGameEvent ? (
                          <SeatedPrintRow
                            event={event}
                            row={row}
                            type={type}
                            key={index}
                            background={index % 2 === 0}
                            className={
                              index % 2 === 0
                                ? "print-table-row-even"
                                : "print-table-row-odd"
                            }
                          />
                        ) : (
                          <SinglesPrintRow
                            event={event}
                            row={row}
                            type={type}
                            key={index}
                            background={index % 2 === 0}
                            className={
                              index % 2 === 0
                                ? "print-table-row-even"
                                : "print-table-row-odd"
                            }
                          />
                        )}
                      </tbody>
                    );
                  })}
              </table>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
